/**
 * Detalle de un comprobante administrativo.
 *
 * Puerto del modal «Venta Nro.» de `views/menu/administrador/comprobantes.php`
 * (`Administrador::get_all_data_venta`): condición de pago, líneas y movimientos
 * de cuenta, con la opción de **actualizar los importes** que el original
 * ofrecía con «Actualizar Precio» (`API/Credit::edition_post`).
 *
 * La actualización recalcula la venta igual que el original: el total pasa a ser
 * la suma de las líneas, la retención se obtiene del porcentaje del cliente y el
 * pendiente es el total menos la retención.
 */

import { BadgeDollarSign, FileDown, Printer, Receipt } from 'lucide-react';
import { useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/http/api-error';

import { useActualizarPrecio, useDetalleComprobante } from '../comprobantes.api';
import { fechaCorta, monto } from '../gestion-formato';

interface Props {
  /** Venta abierta (`null` cierra el diálogo). */
  id: number | null;
  onClose: () => void;
  /** Aviso de éxito que muestra la pantalla. */
  onAviso: (mensaje: string) => void;
}

export function ComprobanteDetalleDialog({ id, onClose, onAviso }: Props) {
  const consulta = useDetalleComprobante(id);
  const actualizar = useActualizarPrecio(id);

  const datos = consulta.data;
  const venta = datos?.venta;

  /** Precios en edición: `{ lineaId: { precio, total } }`. */
  const [precios, setPrecios] = useState<Record<number, { precio: string; total: string }>>({});
  const [retencion, setRetencion] = useState('0');
  const [error, setError] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);

  // La vista se rellena al llegar el detalle (guarda `listo` en lugar de un
  // efecto, como el resto de los diálogos del módulo).
  const [firma, setFirma] = useState('');

  if (datos && firma !== String(datos.venta.id)) {
    setFirma(String(datos.venta.id));
    setPrecios({});
    setRetencion('0');
    setError(null);
    setEditando(false);
  }

  const lineas = datos?.detalle ?? [];

  const totales = lineas.reduce(
    (acumulado, linea) => {
      const edicion = precios[linea.id];

      return {
        total: acumulado.total + Number(edicion?.total ?? linea.total),
      };
    },
    { total: 0 },
  );

  const retencionNumero = Number(retencion) || 0;
  const retencionImporte = Math.round(totales.total * (retencionNumero / 100) * 100) / 100;

  /** Guarda los importes nuevos. */
  const guardar = () => {
    if (id === null) return;

    setError(null);
    actualizar.mutate(
      {
        detalles: lineas.map((linea) => {
          const edicion = precios[linea.id];

          return {
            id: linea.id,
            precio: Number(edicion?.precio ?? linea.precio),
            total: Number(edicion?.total ?? linea.total),
          };
        }),
        total: totales.total,
        retencion_porcentaje: retencionNumero,
      },
      {
        onSuccess: (respuesta) => {
          setEditando(false);
          setPrecios({});
          onAviso(respuesta.mensaje);
          void consulta.refetch();
        },
        onError: (fallo) =>
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudieron actualizar los importes.'),
      },
    );
  };

  const base = datos?.url_facturacion?.replace(/\/api\/?$/, '') ?? null;

  return (
    <Dialog
      open={id !== null}
      onClose={onClose}
      size="xl"
      title={venta ? `Venta ${venta.documento ?? `#${venta.id}`}` : 'Detalle del comprobante'}
      description={
        venta
          ? `${venta.cliente ?? ''} · ${venta.numero_doc ?? ''} · ${venta.tipo_comprobante_nombre ?? ''}`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!base || !venta?.external_id}
            leftIcon={<Printer className="size-4" aria-hidden="true" />}
            onClick={() => {
              if (base && venta?.external_id) {
                window.open(`${base}/print/document/${venta.external_id}/a4`, '_blank', 'noreferrer');
              }
            }}
          >
            Imprimir A4
          </Button>
          {editando ? (
            <Button
              size="sm"
              loading={actualizar.isPending}
              leftIcon={<BadgeDollarSign className="size-4" aria-hidden="true" />}
              onClick={guardar}
            >
              Guardar importes
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              disabled={!datos || venta?.anulado || venta?.estado_sunat !== 'En Validación'}
              leftIcon={<BadgeDollarSign className="size-4" aria-hidden="true" />}
              onClick={() => {
                setPrecios(
                  Object.fromEntries(
                    lineas.map((linea) => [
                      linea.id,
                      { precio: String(linea.precio), total: linea.total.toFixed(2) },
                    ]),
                  ),
                );
                setEditando(true);
              }}
            >
              Actualizar precio
            </Button>
          )}
        </>
      }
    >
      {consulta.isLoading ? <Skeleton className="h-72 w-full" /> : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo leer el comprobante">
          {consulta.error instanceof ApiError ? consulta.error.message : 'Inténtalo de nuevo.'}
        </Alert>
      ) : null}

      {datos && venta ? (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Emisión', fechaCorta(venta.fecha_emision)],
              ['Vencimiento', fechaCorta(venta.fecha_vencimiento)],
              ['Condición de pago', venta.condicion_pago ?? '—'],
              ['Estado en SUNAT', venta.estado_sunat ?? '—'],
            ].map(([etiqueta, valor]) => (
              <div key={String(etiqueta)} className="rounded-card border border-ink-100 bg-white p-3">
                <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                  {etiqueta}
                </p>
                <p className="text-sm font-semibold text-ink-800">{valor}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {[
              ['Total', venta.total],
              ['Retención', venta.withholding_amount],
              ['Pendiente', venta.pending_amount],
              ['Pagado', venta.paidout],
              ['Crédito', venta.credito_monto],
            ].map(([etiqueta, valor]) => (
              <div key={String(etiqueta)} className="rounded-card border border-ink-100 bg-white p-3">
                <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                  {etiqueta}
                </p>
                <p className="text-sm font-semibold text-ink-800">{monto(Number(valor))}</p>
              </div>
            ))}
          </div>

          {venta.observaciones ? (
            <Alert variant="info" title="Observaciones">
              <span className={ICONO_EN_ALERTA}>
                <Receipt className="size-4" aria-hidden="true" />
                {venta.observaciones}
              </span>
            </Alert>
          ) : null}

          {error ? (
            <Alert variant="error" title="No se pudieron guardar los importes">
              {error}
            </Alert>
          ) : null}

          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink-800">
              Detalles de la venta ({lineas.length})
            </h3>
            <div className="overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Producto</th>
                    <th className="px-3 py-2 text-right">Cantidad</th>
                    <th className="px-3 py-2 text-right">P. unitario</th>
                    <th className="px-3 py-2 text-right">P. total</th>
                    <th className="px-3 py-2">Placas</th>
                  </tr>
                </thead>
                <tbody>
                  {lineas.map((linea, indice) => (
                    <tr key={linea.id} className="border-t border-ink-100">
                      <td className="px-3 py-2">{indice + 1}</td>
                      <td className="px-3 py-2">
                        {linea.producto_nombre ?? linea.descripcion_pdf ?? '—'}
                        {linea.igv_exonerated === 'Si' ? (
                          <Badge variant="outline" className="ml-2">
                            Exonerado
                          </Badge>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {linea.cantidad} {linea.unidad ?? ''}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {editando ? (
                          <input
                            className="h-9 w-24 rounded-control border border-ink-200 px-2 text-right text-sm"
                            type="number"
                            step="0.001"
                            value={precios[linea.id]?.precio ?? String(linea.precio)}
                            onChange={(evento) =>
                              setPrecios((previo) => ({
                                ...previo,
                                [linea.id]: {
                                  precio: evento.target.value,
                                  total: previo[linea.id]?.total ?? linea.total.toFixed(2),
                                },
                              }))
                            }
                          />
                        ) : (
                          monto(linea.precio)
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {editando ? (
                          <input
                            className="h-9 w-24 rounded-control border border-ink-200 px-2 text-right text-sm"
                            type="number"
                            step="0.01"
                            value={precios[linea.id]?.total ?? linea.total.toFixed(2)}
                            onChange={(evento) =>
                              setPrecios((previo) => ({
                                ...previo,
                                [linea.id]: {
                                  precio: previo[linea.id]?.precio ?? String(linea.precio),
                                  total: evento.target.value,
                                },
                              }))
                            }
                          />
                        ) : (
                          monto(linea.total)
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-ink-500">
                        {linea.placas_json?.length ? linea.placas_json.join(', ') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-ink-50/60">
                  <tr className="border-t border-ink-100 font-semibold text-ink-800">
                    <td className="px-3 py-2" colSpan={4}>
                      {editando ? 'Nuevo total' : 'Total de las líneas'}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{monto(totales.total)}</td>
                    <td className="px-3 py-2" />
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {editando ? (
            <div className="grid gap-3 sm:grid-cols-3">
              <Input
                label="Retención del cliente (%)"
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={retencion}
                onChange={(evento) => setRetencion(evento.target.value)}
              />
              <div className="rounded-card border border-ink-100 bg-white p-3">
                <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                  Retención
                </p>
                <p className="text-sm font-semibold text-ink-800">{monto(retencionImporte)}</p>
              </div>
              <div className="rounded-card border border-ink-100 bg-white p-3">
                <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                  Pendiente
                </p>
                <p className="text-sm font-semibold text-ink-800">
                  {monto(Math.round((totales.total - retencionImporte) * 100) / 100)}
                </p>
              </div>
            </div>
          ) : null}

          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink-800">
              Movimientos de cuenta ({datos.movimientos.length})
            </h3>

            {datos.movimientos.length === 0 ? (
              <p className="text-sm text-ink-500">La venta no tiene movimientos de cuenta.</p>
            ) : (
              <ul className="divide-y divide-ink-100 overflow-hidden rounded-card border border-ink-100 bg-white">
                {datos.movimientos.map((movimiento) => (
                  <li key={movimiento.movement_id} className="px-3 py-2 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium text-ink-800">
                        #{movimiento.movement_id} · {movimiento.cuenta ?? '—'} ·{' '}
                        {monto(movimiento.movement_amount)}
                      </span>
                      <span className="text-xs text-ink-500">
                        {fechaCorta(movimiento.movement_date)} · {movimiento.estado_reparto} ·{' '}
                        {movimiento.movement_status ?? ''}
                      </span>
                    </div>
                    {movimiento.movement_ref ? (
                      <p className="mt-0.5 text-xs text-ink-500">Ref. {movimiento.movement_ref}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <Alert variant="info" title="De dónde salen las líneas">
            <span className={ICONO_EN_ALERTA}>
              <FileDown className="size-4" aria-hidden="true" />
              Las líneas son `admin_venta_detalle` con su producto y sus placas (`placas_json`), y
              los movimientos son los de `movements_account` cuyo reparto incluye esta venta.
            </span>
          </Alert>
        </div>
      ) : null}
    </Dialog>
  );
}
